import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const InvitationSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.email(),
});

export type Invitation = z.infer<typeof InvitationSchema>;

export async function sendInvitationFn(axiosInstance: Promise<AxiosInstance>, data: Invitation) {
  const instance = await axiosInstance;
  return instance.post('send_invitations', InvitationSchema.parse(data));
}

export function useSendInvitation() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, Invitation>({
    mutationFn: data => sendInvitationFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['users'],
      });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, Invitation, 'sendInvitation'>(
    'sendInvitation',
    mutation,
  );
}
